import { useEffect, useMemo, useState } from 'react';

interface useRequestOptions<T> {
  dependencies?: unknown[];
  skip?: boolean;
  set?: {
    data: (data: T) => void;
    loading?: React.Dispatch<React.SetStateAction<boolean>>;
  };
}

export default function useRequest<T>(
  callback: Function,
  options?: useRequestOptions<T>,
  callbackParams: unknown[] = [],
) {
  const [data, setData] = useState<T>();
  const [loading, setLoading] = useState<boolean>(true);
  const { dependencies, skip } = options ?? {};
  const [error, setError] = useState<Error>();

  const deps = useMemo(() => {
    if (dependencies) {
      return dependencies;
    }
    return [];
  }, [dependencies]);

  useEffect(() => {
    async function fetchData() {
      if (skip) {
        setLoading(false);
        return;
      }

      setLoading?.(true);
      try {
        const response = await callback(...callbackParams);
        setData(response);
        options?.set?.data(response);
      } catch (error: any) {
        console.error(error);
        setError(error);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
    //  eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { data, loading, error };
}
